import { useMutation } from '@apollo/client';
import React, { FC, useCallback, useMemo, useState } from 'react';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { ErrorMessage, Field, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import TextArea from '@atlaskit/textarea';
import { ApprovalStatus, ApprovalType, Task, UpdateStatus } from '@css-xp/apd-common';
import { useApplicationContext } from '@css-xp/base/common-context';
import { EntitlementPageLink, SlackChannelLink } from '../common';
import { ModalTitle } from '../constants/modal-titles';
import { updateTask as UPDATE_TASK } from '../queries/tasks';
import ErrorNotificationMessage from './error-notification-message';
import './modal.scss';
import { useModalNotification } from './use-modal-notification';

interface RequestModalProps {
  task: Task;
  onComplete?: () => void;
  closeModal: () => void;
}

const ApproveModal: FC<RequestModalProps> = ({ task, onComplete, closeModal }) => {
  const [loading, setLoading] = useState<boolean>(false);

  const [updateTask] = useMutation<{ updateTask: { status: UpdateStatus } }>(UPDATE_TASK);

  const userProfile = useApplicationContext();

  const [renderSuccessNotification, renderErrorNotification] = useModalNotification(
    closeModal,
    'approve-modal-notification'
  );

  const entitlementPageLink = useMemo(() => {
    if (task.businessKey && task.entityType) {
      return (
        <EntitlementPageLink entityNumber={task.businessKey} entityType={task.entityType} entityId={task.entityId} />
      );
    }
    return '';
  }, [task]);

  const contents = useMemo(() => {
    if (task.requestType) {
      switch (task.requestType) {
        case ApprovalType.TAX_REFUND:
        case ApprovalType.SUBSCRIPTION_REFUND:
          return {
            modalBody: (
              <p>
                Your decision will be added to the request in the Slack channel{' '}
                <SlackChannelLink url={task.slackMessageUrl}>{task.slackChannel}</SlackChannelLink> and Approval Desk.
                Once you approve, the refund will be initiated.
              </p>
            ),
            notificationMsg: (
              <p>
                You approved the refund request {task.taskNumber} for the invoice {entitlementPageLink}
              </p>
            ),
          };
        case ApprovalType.TRIAL_EXTENSION:
          return {
            modalBody: (
              <p>
                Your decision will be added to the request in the Slack channel{' '}
                <SlackChannelLink url={task.slackMessageUrl}>{task.slackChannel}</SlackChannelLink> and Approval Desk.
                Once you approve, the trial will be extended.
              </p>
            ),
            notificationMsg: (
              <p>
                You approved the trial extension request {task.taskNumber} for the subscription {entitlementPageLink}
              </p>
            ),
          };
        case ApprovalType.ORDER_DISCOUNT:
          return {
            modalBody: (
              <p>
                Your decision will be added to the request in the Slack channel{' '}
                <SlackChannelLink url={task.slackMessageUrl}>{task.slackChannel}</SlackChannelLink> and Approval Desk.
                Once you approve, the discount will be applied.
              </p>
            ),
            notificationMsg: (
              <p>
                You approved the discount request {task.taskNumber} for the subscription {entitlementPageLink}
              </p>
            ),
          };
        case ApprovalType.PRODUCT_DELETION:
          return {
            modalBody: (
              <p>
                Your approval will be added to the request in the Slack channel{' '}
                <SlackChannelLink url={task.slackMessageUrl}>{task.slackChannel}</SlackChannelLink> and Approval Desk.
                Once you approve, the product data will be scheduled for deletion in 15 days' time. After that, the
                subscription can’t be reactivated and the data can’t be recovered.
              </p>
            ),
            notificationMsg: (
              <p>You’ve scheduled the product data deletion for the subscription {entitlementPageLink}</p>
            ),
          };
        case ApprovalType.QUOTE_DISCOUNT:
          return {
            modalBody: (
              <p>
                Your decision will be added to the request in the Slack channel{' '}
                <SlackChannelLink url={task.slackMessageUrl}>{task.slackChannel}</SlackChannelLink> and Approval Desk.
                Once you approve, the discretionary discount will be applied and the quote will be finalized.
              </p>
            ),
            notificationMsg: (
              <p>
                You approved the quote discount request {task.taskNumber} for the quote {entitlementPageLink}
              </p>
            ),
          };
        case ApprovalType.QUOTE_EXTENSION:
          return {
            modalBody: (
              <p>
                Your decision will be added to the request in the Slack channel{' '}
                <SlackChannelLink url={task.slackMessageUrl}>{task.slackChannel}</SlackChannelLink> and Approval Desk.
                Once you approve, the quote’s expiry date will be extended.
              </p>
            ),
            notificationMsg: (
              <p>
                You approved the quote extension request {task.taskNumber} for the quote {entitlementPageLink}
              </p>
            ),
          };
        case ApprovalType.PRICING_PLAN_CHANGE:
          return {
            modalBody: (
              <p>
                Your decision will be added to the request in the Slack channel{' '}
                <SlackChannelLink url={task.slackMessageUrl}>{task.slackChannel}</SlackChannelLink> and Approval Desk.
                Once you approve, the {task.discountApprovalRequiredInPricingPlan && 'discretionary discount and'}{' '}
                pricing plan change will be applied.
              </p>
            ),
            notificationMsg: <p>You approved the selected items.</p>,
          };
      }
    }
    return { modalBody: '', notificationMsg: '' };
  }, [task]);

  const handleFormSubmit = useCallback(
    async ({ comment }: { comment: string }) => {
      setLoading(true);
      const userID = userProfile?.userId?.split('@')?.[0];
      try {
        const result = await updateTask({
          variables: {
            taskId: task.taskId,
            approvalStatus: ApprovalStatus.APPROVED,
            id: userID,
            comment,
          },
        });
        const errorMsg = result?.errors?.[0]?.extensions?.message;
        if (errorMsg) {
          throw new Error(errorMsg);
        }
        const status = result?.data?.updateTask?.status ?? '';
        if (status === 'OK') {
          onComplete?.();
          renderSuccessNotification('Task complete - request approved', contents.notificationMsg);
        }
      } catch (e: any) {
        renderErrorNotification(
          'We’re unable to approve the request',
          <ErrorNotificationMessage errorMessage={e.message} />
        );
      } finally {
        setLoading(false);
      }
    },
    [task.taskId]
  );

  return (
    <Form onSubmit={handleFormSubmit}>
      {({ formProps }) => (
        <div className="modal-form-wrapper" data-testid="approve-modal">
          <form {...formProps}>
            <FormHeader
              title={
                <div className="modal-title-wrapper">
                  <span className="modal-title">
                    {task.requestType === ApprovalType.PRODUCT_DELETION
                      ? ModalTitle.APPROVE_PRODUCT_DELETE
                      : ModalTitle.APPROVE}
                  </span>
                </div>
              }
            />
            {contents.modalBody}
            <FormSection>
              <Field<string, HTMLTextAreaElement>
                name="comment"
                label="Internal comment"
                isRequired={true}
                validate={(value: any) => (value && value.trim().length > 0 ? undefined : 'INVALID')}
              >
                {({ fieldProps, error }) => (
                  <>
                    <TextArea {...fieldProps} placeholder="Add comment" testId="comment-input" isInvalid={!!error} />
                    {error && <ErrorMessage testId="comment-input-error">This field is required</ErrorMessage>}
                  </>
                )}
              </Field>
            </FormSection>
            <FormFooter align="end">
              <ButtonGroup>
                <Button appearance="subtle" onClick={closeModal} testId="cancel-btn">
                  Cancel
                </Button>
                <LoadingButton appearance="primary" autoFocus type="submit" isLoading={loading} testId="submit-btn">
                  {task.requestType === ApprovalType.PRODUCT_DELETION ? 'Approve deletion' : 'Approve'}
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          </form>
        </div>
      )}
    </Form>
  );
};

export default ApproveModal;
